import type { Command, CommandContext } from "./Command";
import { findItem } from "../../inventory/Inventory";
import { getComponent } from "../../world/Entity";
import type { EntityId } from "../../world/Entity";
import type { ArtifactComponent, EntityComponent } from "../../world/components";

function artifactOf(components: readonly EntityComponent[] | undefined): ArtifactComponent | undefined {
  return components?.find((c): c is ArtifactComponent => c.type === "artifact");
}

/** Examine an artifact more closely — emits knowledge progress. */
export class ExamineArtifactCommand implements Command {
  constructor(private readonly entityId: EntityId) {}

  execute(context: CommandContext): void {
    const entity = context.world.getEntity(this.entityId);
    if (entity) {
      const artifactComponent = getComponent(entity, "artifact");
      if (!artifactComponent) throw new Error(`Entity "${this.entityId}" is not an artifact`);
      const definition = context.artifacts.get(artifactComponent.definitionId);
      for (const nodeId of definition?.knowledgeNodes ?? []) {
        context.knowledge.unlock(nodeId, "partiallyUnderstood");
        context.events.emit("knowledge:unlocked", { nodeId, state: "partiallyUnderstood" });
      }
      context.events.emit("artifact:examined", { entityId: this.entityId, artifactId: artifactComponent.definitionId });
      return;
    }

    const item = findItem(context.world.inventory, this.entityId);
    if (!item) throw new Error(`Unknown entity "${this.entityId}"`);
    const components = (item.metadata as { components?: readonly EntityComponent[] } | undefined)?.components;
    const artifactComponent = artifactOf(components);
    if (!artifactComponent) throw new Error(`"${this.entityId}" is not an artifact`);

    const definition = context.artifacts.get(artifactComponent.definitionId);
    for (const nodeId of definition?.knowledgeNodes ?? []) {
      context.knowledge.unlock(nodeId, "partiallyUnderstood");
      context.events.emit("knowledge:unlocked", { nodeId, state: "partiallyUnderstood" });
    }
    context.events.emit("artifact:examined", { entityId: this.entityId, artifactId: artifactComponent.definitionId });
  }
}
